import React from 'react';
import {
  Box,
  Button,
  Chip,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import { IActivityMonitoring } from './activitymonitoring.models';

type Props = {
  row: IActivityMonitoring;
  selected?: boolean;
  expanded?: boolean;
  onSelect: (row: IActivityMonitoring) => void;
  onToggle: (row: IActivityMonitoring) => void;
};

export default class ActivityCard extends React.PureComponent<Props> {
  fmtMoney = (v?: number) => {
    const n = Number(v || 0);

    if (Math.abs(n) >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`;
    if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (Math.abs(n) >= 1_000) return `${(n / 1_000).toFixed(1)}K`;

    return n.toLocaleString();
  };

  healthLabel = (hlt?: string) => {
    switch ((hlt || '').toUpperCase()) {
      case 'HIGH_VARIANCE': return '🔴 High Variance';
      case 'FINANCIAL_AHEAD': return '🟠 Financial Ahead';
      case 'PROGRESS_AHEAD': return '🟡 Progress Ahead';
      default: return '🟢 Aligned';
    }
  };

  render() {
    const { row, selected, expanded, onSelect, onToggle } = this.props;

    const implementation = Number(row.apr || row.rpr || 0);
    const financial = Number(row.fpr || 0);

    return (
      <Paper
        elevation={selected ? 3 : 1}
        sx={{
          p: 2,
          border: selected ? '1px solid' : '1px solid transparent',
          borderColor: selected ? 'primary.main' : 'transparent',
          cursor: 'pointer',
        }}
        onClick={() => onSelect(row)}
      >
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {row.atc}
            </Typography>

            <Typography variant="subtitle1">
              {row.atn}
            </Typography>

            <Typography variant="body2" color="text.secondary">
              {row.pdn} · {row.cyn}
            </Typography>
          </Box>

          <Chip size="small" label={this.healthLabel(row.hlt)} />
        </Stack>

        <Box sx={{ mt: 2 }}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="caption">Implementation</Typography>
            <Typography variant="caption">{implementation.toFixed(1)}%</Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={Math.max(0, Math.min(100, implementation))}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>

        <Box sx={{ mt: 1.25 }}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="caption">Financial Disbursement</Typography>
            <Typography variant="caption">{financial.toFixed(1)}%</Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={Math.max(0, Math.min(100, financial))}
            sx={{ height: 8, borderRadius: 4 }}
          />

          <Typography variant="caption" color="text.secondary">
            Budget: {this.fmtMoney(row.aca)} / {this.fmtMoney(row.pam)}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap">
          <Chip size="small" label={`Tasks ${row.tct || 0}`} />
          <Chip size="small" label={`Completed ${row.ctc || 0}`} />
          <Chip size="small" label={`In Progress ${row.itc || 0}`} />
          <Chip size="small" label={`Waiting ${row.wvc || 0}`} />
          <Chip size="small" label={`Evidence ${row.evc || 0}`} />
          <Chip size="small" label={`Approvals ${row.apc || 0}`} />
          {(row.exc || 0) > 0 && (
            <Chip size="small" color="warning" label={`Exceptions ${row.exc}`} />
          )}
        </Stack>

        <Box sx={{ mt: 1.5 }}>
          <Button
            size="small"
            variant={expanded ? 'contained' : 'outlined'}
            fullWidth
            onClick={(e) => {
              e.stopPropagation();
              onToggle(row);
            }}
          >
            {expanded ? 'Hide Tasks' : 'View Tasks'}
          </Button>
        </Box>
      </Paper>
    );
  }
}